import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, Settings, User } from 'lucide-react';

import { redirectToLogin } from '../api/authNavigation';
import { useLogout } from '../api/mutations/auth';
import { useAuthStore } from '../stores/authStore';
import { ThemeToggle } from './ThemeToggle';

// AppLayout — shell for every authenticated route (PRD §9.4 +
// frontend_architecture.md §2 + B1f PR1, PR3).
//
// Renders the top bar above the route's content. Today the top bar
// hosts:
//   - the brand mark (left, links to /)
//   - the active org name + "switch" affordance (left, only when on an
//     org-scoped route AND the user has 2+ orgs)
//   - theme toggle, the user menu (email → dropdown with Profile +
//     Admin), and a Sign out button (right, inline)
//
// User menu: the email is a dropdown trigger. "Profile" always shows;
// "Admin" shows only when we're inside an org context AND the actor is
// OWNER/ADMIN there (members don't get a door they can't open).
//
// Org switcher (PR3): kept inline + flat — when there's exactly one
// org we show nothing (no decision to make); 2+ orgs render a small
// "Switch" link back to / which is the picker.
//
// `__root.tsx` mounts this for non-public paths only; /login and
// /share/:token render the bare canvas without the shell.

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): React.JSX.Element {
  return (
    <div className="bg-bg text-text min-h-screen">
      <TopBar />
      <div className="mx-auto max-w-screen-2xl px-7 py-7">{children}</div>
    </div>
  );
}

function TopBar(): React.JSX.Element {
  const user = useAuthStore((state) => state.user);
  const logoutMutation = useLogout();
  // `strict: false` lets us read params from any matched route — when
  // we're not on an org-scoped route, `orgSlug` is undefined.
  const { orgSlug } = useParams({ strict: false });

  const orgs = user?.organizations ?? [];
  const currentMembership = orgSlug
    ? orgs.find((m) => m.organization.slug === orgSlug)
    : undefined;
  const currentOrg = currentMembership?.organization;
  const showSwitcher = currentOrg !== undefined && orgs.length >= 2;
  const isOrgAdmin =
    currentMembership !== undefined &&
    (currentMembership.role === 'owner' || currentMembership.role === 'admin');

  const onLogout = (): void => {
    logoutMutation.mutate(undefined, {
      onSettled: () => {
        redirectToLogin('/login');
      },
    });
  };

  return (
    <header className="border-border bg-bg-elevated border-b">
      <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-7 py-3">
        <div className="flex items-center gap-3">
          <Link
            to="/"
            className="text-text-faint hover:text-text text-11 uppercase tracking-wider transition-colors"
          >
            KansiTRK
          </Link>
          {currentOrg && (
            <>
              <span aria-hidden="true" className="text-text-faint">
                /
              </span>
              <span className="text-text text-13 font-semibold">
                {currentOrg.name}
              </span>
              {showSwitcher && (
                <Link
                  to="/"
                  className="text-text-muted hover:text-text border-border rounded-2 border px-2 py-0.5 text-11 transition-colors"
                >
                  Switch
                </Link>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <ThemeToggle />
          {user && (
            <UserMenu
              email={user.email}
              adminOrgSlug={isOrgAdmin && orgSlug ? orgSlug : null}
            />
          )}
          <button
            type="button"
            onClick={onLogout}
            disabled={logoutMutation.isPending}
            className="border-border text-text-muted hover:text-text rounded-2 border px-3 py-1 text-11 transition-colors disabled:opacity-60"
          >
            {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      </div>
    </header>
  );
}

// Hand-rolled dropdown (matching the codebase's no-Radix dialogs):
// closes on outside click, Escape, and after choosing an item.
function UserMenu({
  email,
  adminOrgSlug,
}: {
  email: string;
  adminOrgSlug: string | null;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent): void {
      if (
        rootRef.current !== null &&
        e.target instanceof Node &&
        !rootRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="topbar-user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-text-muted hover:text-text inline-flex items-center gap-1 font-mono text-11 transition-colors"
      >
        {email}
        <ChevronDown
          width={12}
          height={12}
          strokeWidth={2}
          aria-hidden="true"
          className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
        />
      </button>
      {open && (
        <nav
          role="menu"
          data-testid="topbar-user-menu-list"
          aria-label="User menu"
          className="border-border bg-bg-elevated rounded-2 absolute right-0 top-full z-30 mt-2 flex w-44 flex-col border py-1 shadow-xl"
        >
          <Link
            to="/settings/profile"
            role="menuitem"
            data-testid="topbar-user-menu-profile"
            onClick={() => setOpen(false)}
            className="text-text-muted hover:text-text hover:bg-bg flex items-center gap-2 px-3 py-2 text-13 transition-colors"
          >
            <User width={14} height={14} strokeWidth={2} aria-hidden="true" />
            Profile
          </Link>
          {adminOrgSlug !== null && (
            <Link
              to="/orgs/$orgSlug/admin/projects"
              params={{ orgSlug: adminOrgSlug }}
              role="menuitem"
              data-testid="topbar-user-menu-admin"
              onClick={() => setOpen(false)}
              className="text-text-muted hover:text-text hover:bg-bg flex items-center gap-2 px-3 py-2 text-13 transition-colors"
            >
              <Settings
                width={14}
                height={14}
                strokeWidth={2}
                aria-hidden="true"
              />
              Admin
            </Link>
          )}
        </nav>
      )}
    </div>
  );
}
